<template>
	<div
		class="xsidebar"
		:class="[store.state.mobileMenu ? 'xsidebar-visible' : '']">
		<div class="xsidebar-head">
			<router-link to="/">
				<img
					src="@/assets/images/logo-student-choice.png"
					alt="Student Choice Logo" />
			</router-link>
			<h1>Loan Origination System</h1>
		</div>

    <nav class="xsidebar-nav">
      <div v-if="userType == 'Admin'">
        <h3>Menu</h3>
        <router-link to="/dashboard" class="icon-home" style="display: none"
          >Dashboard</router-link>
        <PermissionsWrapper
          :permissionIds="[Permissions.Applications]"
          :permissionLevel="PermissionLevels.Read">
          <router-link
            to="/applications"
            class="icon-doc-text"
            :class="{ 'router-link-active': $route.name == 'Application' }"
            >Applications</router-link>
        </PermissionsWrapper>
					
					<PermissionsWrapper
						:permissionIds="[Permissions.CertAndDisburse]"
						:permissionLevel="PermissionLevels.Read">  
						<router-link
	          to="/certifications"
	          class="icon-money-checkmark-right"
	          :class="{ 'router-link-active': $route.name == 'Certification' }"
	          >Certifications</router-link
	        ></PermissionsWrapper>
      
				<PermissionsWrapper 
					:permissionIds="[Permissions.CertAndDisburse]"
					:permissionLevel="PermissionLevels.Read">
					<router-link 
						to="/disbursements" 
						class="icon-bank-arrow-right" 
						:class="{'router-link-active': $route.name == 'Disbursement'}">
					Disbursements
					</router-link>
				</PermissionsWrapper>

				<PermissionsWrapper
					:permissionIds="[Permissions.Loans]"
					:permissionLevel="PermissionLevels.Read">
					<router-link
						to="/loans"
						class="icon-money"
						:class="{ 'router-link-active': $route.name == 'Loan' }">
						Loans
					</router-link>
				</PermissionsWrapper>
				<PermissionsWrapper
					:permissionIds="[Permissions.DisbursementBatches]"
					:permissionLevel="PermissionLevels.Read">
					<router-link
						to="/batches"
						class="icon-money-1"
						:class="{
							'router-link-active': isActive('Batches', ['disbursementbatches', 'payoff-batches']),
						}">
						Disbursement Batches
					</router-link>
					<div v-if="batchId" class="batch-id-display">
						<font-awesome-icon
							:icon="['fas', 'chevron-left']"
							class="chevron-left" />
						<font-awesome-icon
							:icon="['fas', 'coins']"
							class="batch-edit-icon" />
						Batch ID: {{ batchId }}
					</div>					
				</PermissionsWrapper>
				<PermissionsWrapper v-if="showPowerBI"
					:permissionIds="[Permissions.Reports]"
					:permissionLevel="PermissionLevels.Read">
					<router-link to="/reports" class="icon-suitcase">
						Data Warehouse
					</router-link>
				</PermissionsWrapper>
				<!-- SOME LEGACY CODE WAS MARKED WITH "style='display: none'" & NOT CURRENTLY VIEWABLE BUT MAY BE USED IN LATER ITERATIONS ???-->
				<!-- <div style="display: none">
          <h3>Configuration</h3>
          <router-link to="/administration" class="icon-sliders"
            >Administration</router-link>
          <router-link to="/lenders" class="icon-money-1">Lenders</router-link>
          <router-link to="/bureaus" class="icon-bank">Bureaus</router-link>
          <router-link to="/programs" class="icon-suitcase"
            >Programs</router-link>
          <router-link to="/pricing" class="icon-tag">Pricing</router-link>
        </div> -->
			</div>
			{{ batchData }}
			<div v-if="userType == 'Manager'">
				<router-link to="/dashboard" class="icon-home">Dashboard</router-link>
				<router-link
					to="/applications"
					class="icon-doc-text"
					:class="{ 'router-link-active': $route.name == 'Application' }">
					Applications
				</router-link>
				<router-link to="/loans" class="icon-money" style="display: none">
					Loans
				</router-link>
				<div style="display: none">
					<h3>Configuration</h3>
					<router-link to="/administration">Administration</router-link>
				</div>
			</div>
			<div v-if="userType == 'Concierge'">
				<router-link to="/dashboard" class="icon-home">Dashboard</router-link>
				<router-link
					to="/applications"
					class="icon-doc-text"
					:class="{ 'router-link-active': $route.name == 'Application' }">
					Applications
				</router-link>
				<router-link to="/loans" class="icon-money" style="display: none">
					Loans
				</router-link>
			</div>
			<div v-if="userType == 'Processor'">
				<router-link to="/dashboard" class="icon-home">Dashboard</router-link>
				<router-link to="/processing" class="icon-doc-text">
					Processing
				</router-link>
			</div>			
		</nav>
	</div>
</template>

<script setup lang="ts">
	import { ref, computed, watch, onMounted } from "vue";
	import store from "@/store";
	import { useRoute, useRouter } from "vue-router";
	import { Permissions, PermissionLevels } from "@/models/permissions";
	import PermissionsWrapper from "../wrappers/PermissionsWrapper.vue";
	import { useApplicantsStore } from "@/store/store/applicantsStore";
	import { useBatchesStore } from "@/store/store/batchesStore";
	import { useFeatures } from "@/use/useFeatures";

	const batchesStore = useBatchesStore();
	const route = useRoute();
	const router = useRouter();
	const batchId = computed(() => batchesStore.getBatchId());
	const batchData = ref<{
	disbursementBatch: any;
	disbursements: any[];
	} | null>(null);

	const userType = ref<"Admin" | "Manager" | "Concierge" | "Processor">(
		"Admin"
	);

	const { features, loadFeatures } = useFeatures();

	const canEdit = useApplicantsStore();

	const showPowerBI = computed(() => features.value?.powerBIReporting === true);

	function isActive(routeName: string, matchPaths: string[] = []) {
		const currentRoute = route.name;
		const currentPath = route.path;

		const isRouteNameMatch = currentRoute === routeName;
		const isPathMatch = matchPaths.some(path => currentPath.includes(path));

		return isRouteNameMatch || isPathMatch;
	}

	onMounted(() => {
		loadFeatures();
	});
</script>

<style scoped>
	.batch-edit-icon {
		margin-left: 15px;
		margin-right: 10px;
	}

	.chevron-left {
		margin-left: 15px;
		margin-right: 20px;
	}

	.batch-id-display {
		display: flex;
		align-items: center;
		cursor: default;
		background-color: #d4d6dd;
		padding: 20px;
		font-size: 14px;
	}
</style>